import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { SaveFilterModal } from './SaveFilterModal';
import { SavedFiltersDropdown } from './SavedFiltersDropdown';
import { toast } from 'sonner';
import { savedFiltersService } from '@/services/saved-filters-service';
import { dataService, isDemoMode } from '@/services/data-service';
import type { PendingFilters, SavedFilter } from '@/contexts/RightPanelContext';
import type { Location } from '@/types/settings';

// Fallback mock data for dropdowns (used when API fails or in demo mode)
const AUDIT_TYPES = ['Full Audit', 'Spot Check', 'Verification', 'Reconciliation', 'Compliance'];
const STATUSES = ['Completed', 'In Progress', 'Cancelled', 'Pending Review'];
const FALLBACK_LOCATIONS = ['Head Office', 'Warehouse A', 'Warehouse B', 'Branch 1', 'Branch 2', 'Remote'];
const AUDITORS = ['John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'Charlie Brown'];
const CREATED_BY = ['John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'System'];

export const AuditHistoryFiltersTab: React.FC = () => {
  const {
    setActiveFilterCount,
    pendingFilters,
    setPendingFilters,
    appliedFilters,
    applyFilters,
    clearAllFilters,
    savedFilters,
    setSavedFilters,
    loadSavedFilter,
  } = useRightPanel();

  // Audit Type
  const [selectedAuditTypes, setSelectedAuditTypes] = useState<string[]>([]);

  // Status
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);

  // Date Range
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();

  // Completion Date Range
  const [completionDateFrom, setCompletionDateFrom] = useState<Date | undefined>();
  const [completionDateTo, setCompletionDateTo] = useState<Date | undefined>();

  // Location
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  // Auditor
  const [selectedAuditors, setSelectedAuditors] = useState<string[]>([]);

  // Completion Rate Range
  const [completionRateMin, setCompletionRateMin] = useState('');
  const [completionRateMax, setCompletionRateMax] = useState('');

  // Exception Count Range
  const [exceptionCountMin, setExceptionCountMin] = useState('');
  const [exceptionCountMax, setExceptionCountMax] = useState('');

  // Total Assets Range
  const [totalAssetsMin, setTotalAssetsMin] = useState('');
  const [totalAssetsMax, setTotalAssetsMax] = useState('');

  // Created By
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<string[]>([]);

  // Audit Title Search
  const [auditTitleSearch, setAuditTitleSearch] = useState('');

  // Audit Number Search
  const [auditNumberSearch, setAuditNumberSearch] = useState('');

  // Save filter modal state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);

  // Selected saved filter
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);

  // Loading state for API operations
  const [, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // API-fetched filter options
  const [apiLocations, setApiLocations] = useState<Location[]>([]);
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);

  // Ref to track if we're loading from external source
  const isLoadingFromExternal = React.useRef(false);

  // Fetch filter options from API on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setIsLoadingFilterOptions(true);
      try {
        const locations = await dataService.getLocations();
        setApiLocations(locations);
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      } finally {
        setIsLoadingFilterOptions(false);
      }
    };
    fetchFilterOptions();
  }, []);

  // Compute filter options from API data (only use fallback in demo mode)
  const locationOptions = useMemo(() => {
    if (apiLocations.length > 0) {
      return apiLocations.map(loc => ({ label: loc.name, value: loc.name }));
    }
    if (isDemoMode()) {
      return FALLBACK_LOCATIONS.map(loc => ({ label: loc, value: loc }));
    }
    return [];
  }, [apiLocations]);

  // Load saved filters from API on component mount
  const loadSavedFiltersFromApi = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters = await savedFiltersService.getSavedFilters();
      setSavedFilters(filters);
    } catch (error) {
      console.error('Failed to load saved filters:', error);
      toast.error('Failed to load saved filters');
    } finally {
      setIsLoading(false);
    }
  }, [setSavedFilters]);

  // Load saved filters on mount
  useEffect(() => {
    loadSavedFiltersFromApi();
  }, [loadSavedFiltersFromApi]);

  // Update pending filters whenever local state changes
  useEffect(() => {
    if (isLoadingFromExternal.current) {
      return;
    }

    // Map to audit-specific properties in PendingFilters
    const filters: PendingFilters = {
      // Keep asset properties empty for audit history filters
      selectedCategories: [],
      selectedStatuses: [],
      selectedLocations,
      acquisitionDateRange: { from: undefined, to: undefined },
      acquisitionCostMin: '',
      acquisitionCostMax: '',
      netBookValueMin: '',
      netBookValueMax: '',
      selectedAssignedTo: [],
      selectedDepreciationMethods: [],
      warrantyExpiryDateRange: { from: undefined, to: undefined },
      selectedManufacturers: [],
      // Audit history-specific properties
      selectedAuditTypes,
      selectedAuditStatuses: selectedStatuses,
      auditScheduledDateRange: { from: dateFrom, to: dateTo },
      completionDateRange: { from: completionDateFrom, to: completionDateTo },
      selectedAuditors,
      completionRateMin: completionRateMin ? parseFloat(completionRateMin) : undefined,
      completionRateMax: completionRateMax ? parseFloat(completionRateMax) : undefined,
      exceptionCountMin: exceptionCountMin ? parseFloat(exceptionCountMin) : undefined,
      exceptionCountMax: exceptionCountMax ? parseFloat(exceptionCountMax) : undefined,
      totalAssetsMin: totalAssetsMin ? parseFloat(totalAssetsMin) : undefined,
      totalAssetsMax: totalAssetsMax ? parseFloat(totalAssetsMax) : undefined,
      auditTitleSearch,
      auditNumberSearch,
    };
    setPendingFilters(filters);
  }, [
    selectedAuditTypes,
    selectedStatuses,
    dateFrom,
    dateTo,
    completionDateFrom,
    completionDateTo,
    selectedLocations,
    selectedAuditors,
    completionRateMin,
    completionRateMax,
    exceptionCountMin,
    exceptionCountMax,
    totalAssetsMin,
    totalAssetsMax,
    selectedCreatedBy,
    auditTitleSearch,
    auditNumberSearch,
    setPendingFilters,
  ]);

  // Load pending filters into local state when they change from external source
  useEffect(() => {
    if (pendingFilters) {
      isLoadingFromExternal.current = true;
      // Load from audit history-specific properties
      setSelectedAuditTypes(pendingFilters.selectedAuditTypes || []);
      setSelectedStatuses(pendingFilters.selectedAuditStatuses || []);
      setSelectedLocations(pendingFilters.selectedLocations);
      setDateFrom(pendingFilters.auditScheduledDateRange?.from);
      setDateTo(pendingFilters.auditScheduledDateRange?.to);
      setCompletionRateMin(pendingFilters.completionRateMin?.toString() || '');
      setCompletionRateMax(pendingFilters.completionRateMax?.toString() || '');
      setExceptionCountMin(pendingFilters.exceptionCountMin?.toString() || '');
      setExceptionCountMax(pendingFilters.exceptionCountMax?.toString() || '');
      setSelectedAuditors(pendingFilters.selectedAuditors || []);
      setCompletionDateFrom(pendingFilters.completionDateRange?.from);
      setCompletionDateTo(pendingFilters.completionDateRange?.to);
      setTotalAssetsMin(pendingFilters.totalAssetsMin?.toString() || '');
      setTotalAssetsMax(pendingFilters.totalAssetsMax?.toString() || '');
      setAuditTitleSearch(pendingFilters.auditTitleSearch || '');
      setAuditNumberSearch(pendingFilters.auditNumberSearch || '');
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters]);

  // Clear local state when filters are cleared
  useEffect(() => {
    if (!pendingFilters && !appliedFilters) {
      isLoadingFromExternal.current = true;
      setSelectedAuditTypes([]);
      setSelectedStatuses([]);
      setDateFrom(undefined);
      setDateTo(undefined);
      setCompletionDateFrom(undefined);
      setCompletionDateTo(undefined);
      setSelectedLocations([]);
      setSelectedAuditors([]);
      setCompletionRateMin('');
      setCompletionRateMax('');
      setExceptionCountMin('');
      setExceptionCountMax('');
      setTotalAssetsMin('');
      setTotalAssetsMax('');
      setSelectedCreatedBy([]);
      setAuditTitleSearch('');
      setAuditNumberSearch('');
      setSelectedSavedFilterId(null);
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [pendingFilters, appliedFilters]);

  const handleClearAll = () => {
    isLoadingFromExternal.current = true;
    setSelectedAuditTypes([]);
    setSelectedStatuses([]);
    setDateFrom(undefined);
    setDateTo(undefined);
    setCompletionDateFrom(undefined);
    setCompletionDateTo(undefined);
    setSelectedLocations([]);
    setSelectedAuditors([]);
    setCompletionRateMin('');
    setCompletionRateMax('');
    setExceptionCountMin('');
    setExceptionCountMax('');
    setTotalAssetsMin('');
    setTotalAssetsMax('');
    setSelectedCreatedBy([]);
    setAuditTitleSearch('');
    setAuditNumberSearch('');
    setSelectedSavedFilterId(null);
    clearAllFilters();
    setTimeout(() => {
      isLoadingFromExternal.current = false;
    }, 0);
  };

  const activeFilterCount =
    selectedAuditTypes.length +
    selectedStatuses.length +
    (dateFrom || dateTo ? 1 : 0) +
    (completionDateFrom || completionDateTo ? 1 : 0) +
    selectedLocations.length +
    selectedAuditors.length +
    (completionRateMin || completionRateMax ? 1 : 0) +
    (exceptionCountMin || exceptionCountMax ? 1 : 0) +
    (totalAssetsMin || totalAssetsMax ? 1 : 0) +
    selectedCreatedBy.length +
    (auditTitleSearch ? 1 : 0) +
    (auditNumberSearch ? 1 : 0);

  // Update the context with the active filter count
  useEffect(() => {
    setActiveFilterCount(activeFilterCount);
  }, [activeFilterCount, setActiveFilterCount]);

  const hasFilterChanges = () => {
    // If no pending filters exist, no changes can be made
    if (!pendingFilters) return false;

    // If no filters are active and nothing is applied, no changes
    if (!appliedFilters && activeFilterCount === 0) return false;

    // If we have a selected saved filter, compare pending with the saved filter
    if (selectedSavedFilterId) {
      const selectedFilter = savedFilters.find(f => f.id === selectedSavedFilterId);
      if (selectedFilter) {
        // Compare current pending filters with the saved filter
        return JSON.stringify(pendingFilters) !== JSON.stringify(selectedFilter.filters);
      }
    }

    // If there are applied filters but no active filters (user cleared everything), that's a change
    if (appliedFilters && activeFilterCount === 0) return true;

    // Otherwise, compare pending with applied filters
    if (!appliedFilters && activeFilterCount > 0) return true;
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
  };

  const handleApplyFilters = () => {
    applyFilters();
  };

  const handleSaveAndApply = () => {
    if (activeFilterCount === 0) {
      toast.error('Please configure at least one filter before saving');
      return;
    }
    setSaveModalOpen(true);
  };

  const handleSaveFilter = async (name: string) => {
    if (!pendingFilters) return;

    setIsSaving(true);
    try {
      if (editingFilter) {
        const updatedFilter = await savedFiltersService.updateSavedFilter(
          editingFilter.id,
          name,
          pendingFilters
        );
        if (updatedFilter) {
          const updatedFilters = savedFilters.map((filter) =>
            filter.id === editingFilter.id ? updatedFilter : filter
          );
          setSavedFilters(updatedFilters);
          toast.success(`Filter '${name}' updated successfully`);
        } else {
          toast.error('Filter not found');
        }
        setEditingFilter(null);
      } else {
        const newFilter = await savedFiltersService.createSavedFilter(name, pendingFilters);
        setSavedFilters([...savedFilters, newFilter]);
        toast.success(`Filter '${name}' saved successfully`);
        setSelectedSavedFilterId(newFilter.id);
      }
      applyFilters();
    } catch (error) {
      console.error('Failed to save filter:', error);
      toast.error('Failed to save filter');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectSavedFilter = (filterId: string | null) => {
    setSelectedSavedFilterId(filterId);
    if (filterId) {
      loadSavedFilter(filterId);
      // Auto-apply the saved filter immediately
      setTimeout(() => {
        applyFilters();
      }, 0);
    } else {
      handleClearAll();
    }
  };

  const handleEditFilter = (filter: SavedFilter) => {
    setEditingFilter(filter);
    setSaveModalOpen(true);
  };

  const handleDeleteFilter = async (filterId: string) => {
    try {
      const success = await savedFiltersService.deleteSavedFilter(filterId);
      if (success) {
        setSavedFilters(savedFilters.filter((f) => f.id !== filterId));
        if (selectedSavedFilterId === filterId) {
          setSelectedSavedFilterId(null);
        }
        toast.success('Filter deleted successfully');
      } else {
        toast.error('Filter not found');
      }
    } catch (error) {
      console.error('Failed to delete filter:', error);
      toast.error('Failed to delete filter');
    }
  };

  const handleDuplicateFilter = async (filter: SavedFilter) => {
    try {
      const duplicatedFilter = await savedFiltersService.createSavedFilter(
        `${filter.name} (Copy)`,
        filter.filters
      );
      setSavedFilters([...savedFilters, duplicatedFilter]);
      toast.success(`Filter '${duplicatedFilter.name}' created`);
      setSelectedSavedFilterId(duplicatedFilter.id);
      loadSavedFilter(duplicatedFilter.id);
    } catch (error) {
      console.error('Failed to duplicate filter:', error);
      toast.error('Failed to duplicate filter');
    }
  };

  const hasUnsavedChanges = hasFilterChanges();

  return (
    <div className="flex flex-col h-full">
      {/* Saved Filters Dropdown */}
      <div className="border-b bg-[#f8fafc] dark:bg-[#0f172b]" style={{ borderColor: '#eee' }}>
        <SavedFiltersDropdown
          savedFilters={savedFilters}
          selectedFilterId={selectedSavedFilterId}
          onSelectFilter={handleSelectSavedFilter}
          onEditFilter={handleEditFilter}
          onDeleteFilter={handleDeleteFilter}
          onDuplicateFilter={handleDuplicateFilter}
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">

        {/* Audit Number Search */}
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-sm font-semibold">Audit Number</Label>
            <p className="text-xs text-muted-foreground">Search by audit number</p>
          </div>
          <Input
            placeholder="Search by audit number..."
            value={auditNumberSearch}
            onChange={(e) => setAuditNumberSearch(e.target.value)}
            className="h-9"
          />
        </div>

        <Separator />

        {/* Audit Title Search */}
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-sm font-semibold">Audit Title</Label>
            <p className="text-xs text-muted-foreground">Search by audit title</p>
          </div>
          <Input
            placeholder="Search by title..."
            value={auditTitleSearch}
            onChange={(e) => setAuditTitleSearch(e.target.value)}
            className="h-9"
          />
        </div>

        <Separator />

        {/* Audit Type Filter */}
        <div className="space-y-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Audit Type</Label>
              {selectedAuditTypes.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedAuditTypes.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Type of audit to filter</p>
          </div>

          <MultiSelect
            options={AUDIT_TYPES.map(type => ({ label: type, value: type }))}
            value={selectedAuditTypes}
            onValueChange={setSelectedAuditTypes}
            placeholder="Select audit types..."
          />
        </div>

        <Separator />

        {/* Status Filter */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Status</Label>
            {selectedStatuses.length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {selectedStatuses.length}
              </Badge>
            )}
          </div>

          <MultiSelect
            options={STATUSES.map(status => ({ label: status, value: status }))}
            value={selectedStatuses}
            onValueChange={setSelectedStatuses}
            placeholder="Select statuses..."
          />
        </div>

        <Separator />

        {/* Date Range Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Audit Date</Label>

          <DateRangePicker
            from={dateFrom}
            to={dateTo}
            onFromChange={setDateFrom}
            onToChange={setDateTo}
          />
        </div>

        <Separator />

        {/* Completion Date Range Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Completion Date</Label>

          <DateRangePicker
            from={completionDateFrom}
            to={completionDateTo}
            onFromChange={setCompletionDateFrom}
            onToChange={setCompletionDateTo}
          />
        </div>

        <Separator />

        {/* Location Filter */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Location</Label>
            {selectedLocations.length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {selectedLocations.length}
              </Badge>
            )}
          </div>

          <MultiSelect
            options={locationOptions}
            value={selectedLocations}
            onValueChange={setSelectedLocations}
            placeholder={isLoadingFilterOptions ? "Loading..." : "Select locations..."}
          />
        </div>

        <Separator />

        {/* Auditor Filter */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Auditor</Label>
            {selectedAuditors.length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {selectedAuditors.length}
              </Badge>
            )}
          </div>

          <MultiSelect
            options={AUDITORS.map(auditor => ({ label: auditor, value: auditor }))}
            value={selectedAuditors}
            onValueChange={setSelectedAuditors}
            placeholder="Select auditors..."
          />
        </div>

        <Separator />

        {/* Completion Rate Range Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Completion Rate (%)</Label>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="completion-min" className="text-xs text-muted-foreground">Min</Label>
              <Input
                id="completion-min"
                type="number"
                placeholder="0"
                min="0"
                max="100"
                value={completionRateMin}
                onChange={(e) => setCompletionRateMin(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="completion-max" className="text-xs text-muted-foreground">Max</Label>
              <Input
                id="completion-max"
                type="number"
                placeholder="100"
                min="0"
                max="100"
                value={completionRateMax}
                onChange={(e) => setCompletionRateMax(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Exception Count Range Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Exception Count</Label>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="exception-min" className="text-xs text-muted-foreground">Min</Label>
              <Input
                id="exception-min"
                type="number"
                placeholder="0"
                value={exceptionCountMin}
                onChange={(e) => setExceptionCountMin(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="exception-max" className="text-xs text-muted-foreground">Max</Label>
              <Input
                id="exception-max"
                type="number"
                placeholder="999"
                value={exceptionCountMax}
                onChange={(e) => setExceptionCountMax(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Total Assets Range Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Total Assets</Label>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="assets-min" className="text-xs text-muted-foreground">Min</Label>
              <Input
                id="assets-min"
                type="number"
                placeholder="0"
                value={totalAssetsMin}
                onChange={(e) => setTotalAssetsMin(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="assets-max" className="text-xs text-muted-foreground">Max</Label>
              <Input
                id="assets-max"
                type="number"
                placeholder="9999"
                value={totalAssetsMax}
                onChange={(e) => setTotalAssetsMax(e.target.value)}
                className="h-9"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Created By Filter */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Created By</Label>
            {selectedCreatedBy.length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                {selectedCreatedBy.length}
              </Badge>
            )}
          </div>

          <MultiSelect
            options={CREATED_BY.map(user => ({ label: user, value: user }))}
            value={selectedCreatedBy}
            onValueChange={setSelectedCreatedBy}
            placeholder="Select users..."
          />
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 bg-background border-t shadow-lg p-4">
        {hasUnsavedChanges && (
          <div className="mb-3 text-center">
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndApply}
            disabled={activeFilterCount === 0 || isSaving}
            className="flex-1"
          >
            {isSaving ? 'Saving...' : 'Save and Apply'}
          </Button>
          <Button
            onClick={handleApplyFilters}
            disabled={!hasUnsavedChanges}
            className="flex-1"
          >
            Apply Filter
          </Button>
        </div>
      </div>

      {/* Save Filter Modal */}
      {pendingFilters && (
        <SaveFilterModal
          open={saveModalOpen}
          onOpenChange={setSaveModalOpen}
          pendingFilters={pendingFilters}
          existingFilters={savedFilters}
          onSave={handleSaveFilter}
          editingFilter={editingFilter}
        />
      )}
    </div>
  );
};
